import React, { useMemo, useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { Wallet, Users, CreditCard, Home, FileText, LogOut, User as UserIcon, TrendingDown, TrendingUp, BarChart3, PiggyBank, UserCog, Tags, Receipt, PieChart, Menu, X, ShieldCheck, ScrollText } from 'lucide-react';
import { EnvironmentBanner } from './EnvironmentBanner';
import { authTokenStorage, authUserStorage, getUsernameFromToken, getTwoFactorEnabledFromToken } from '../services/api';

interface LayoutProps {
    children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [user, setUser] = useState(() => authUserStorage.get());
    const [twoFactorSuggestionDismissed, setTwoFactorSuggestionDismissed] = useState(() => {
        try {
            return localStorage.getItem('2fa_suggestion_dismissed') === 'true';
        } catch {
            return false;
        }
    });

    const dismissTwoFactorSuggestion = () => {
        setTwoFactorSuggestionDismissed(true);
        try {
            localStorage.setItem('2fa_suggestion_dismissed', 'true');
        } catch {}
    };

    // Check username from storage first, then fallback to token
    const username = user?.username || getUsernameFromToken();
    const isAdmin = username === 'admin';

    const showTwoFactorSuggestion = user && !isAdmin && !getTwoFactorEnabledFromToken() && !twoFactorSuggestionDismissed;

    // Redirect admin users away from non-admin routes
    useEffect(() => {
        const isAllowedAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/security';
        if (isAdmin && !isAllowedAdminRoute) {
            navigate('/admin', { replace: true });
        }
    }, [isAdmin, location.pathname, navigate]);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const syncUser = () => setUser(authUserStorage.get());

        window.addEventListener('auth-user-updated', syncUser);
        window.addEventListener('storage', syncUser);

        return () => {
            window.removeEventListener('auth-user-updated', syncUser);
            window.removeEventListener('storage', syncUser);
        };
    }, []);

    const handleLogout = () => {
        authTokenStorage.clear();
        authUserStorage.clear();
        navigate('/login', { replace: true });
    };

    const initials = useMemo(() => {
        const name = user?.name || '';
        if (!name) return 'U';
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase();
    }, [user]);

    const menuItems = isAdmin
        ? [
            { path: '/admin', label: 'Home', icon: Home },
            { path: '/admin/accounts', label: 'Contas', icon: Users },
            { path: '/admin/user', label: 'Usuários', icon: UserCog },
            { path: '/admin/logs', label: 'Logs', icon: ScrollText },
            { path: '/security', label: 'Segurança', icon: ShieldCheck },
        ]
        : [
            { path: '/', label: 'Home', icon: Home },
            { path: '/profile', label: 'Minha Conta', icon: UserIcon },
            { path: '/security', label: 'Segurança', icon: ShieldCheck },
            { path: '/payees', label: 'Favorecidos', icon: Users },
            { path: '/payers', label: 'Pagadores', icon: UserIcon },
            { path: '/bank-accounts', label: 'Contas Bancárias', icon: Wallet },
            { path: '/payment-methods', label: 'Métodos de Pagamento', icon: CreditCard },
            { path: '/accounts-payable', label: 'Contas a Pagar', icon: TrendingDown },
            { path: '/accounts-receivable', label: 'Contas a Receber', icon: TrendingUp },
            { path: '/bank-statement', label: 'Extrato Bancário', icon: FileText },
            { path: '/investments', label: 'Investimentos', icon: PiggyBank },
            { path: '/analytics', label: 'Análises', icon: BarChart3 },
            { path: '/expense-categories', label: 'Categorias de gastos', icon: Tags },
            { path: '/expenses', label: 'Gastos', icon: Receipt },
            { path: '/expense-report', label: 'Relatório de gastos', icon: PieChart },
        ];

    return (
        <div className="min-h-screen bg-secondary-50">
            {/* Environment Banner */}
            <EnvironmentBanner />

            {/* 2FA Suggestion Banner */}
            {showTwoFactorSuggestion && (
                <div className="bg-blue-500 text-white py-2 px-4 flex items-center justify-between shadow-md">
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="font-semibold text-sm">Para maior segurança, ative a autenticação de dois fatores.</span>
                        <Link to="/security" className="underline hover:no-underline">Configurar</Link>
                    </div>
                    <button
                        onClick={dismissTwoFactorSuggestion}
                        className="text-white hover:text-gray-200"
                        aria-label="Fechar sugestão"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Header */}
            <header className="relative bg-white shadow-sm border-b border-secondary-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center">
                            <Wallet className="h-8 w-8 text-primary-600" />
                            <h1 className="ml-2 text-xl font-bold text-secondary-900">My Finances</h1>
                        </div>
                        <button
                            type="button"
                            className="lg:hidden p-2 rounded-md text-secondary-700 hover:bg-secondary-100"
                            onClick={() => setMobileMenuOpen((prev) => !prev)}
                            aria-label="Abrir menu"
                        >
                            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
                {user && (
                    <div className="hidden lg:flex absolute inset-y-0 right-4 sm:right-6 lg:right-8 items-center">
                        <button className="flex items-center space-x-3 px-2 py-1 rounded-lg hover:bg-secondary-100 transition-colors">
                            <div className="h-9 w-9 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-semibold">
                                {initials}
                            </div>
                            <div className="hidden sm:block text-left">
                                <div className="text-sm font-medium text-secondary-900 leading-tight">{user.name}</div>
                                {user.username && <div className="text-xs text-secondary-600">@{user.username}</div>}
                            </div>
                        </button>
                    </div>
                )}
            </header>

            <div className="flex">
                {/* Sidebar */}
                <nav
                    className={`${mobileMenuOpen ? 'fixed inset-0 z-40 bg-black/40 lg:bg-transparent' : 'hidden'} lg:block lg:static`}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <div className="w-72 max-w-[85vw] bg-white shadow-sm min-h-screen lg:w-64" onClick={(e) => e.stopPropagation()}>
                    <div className="p-4">
                        {user && (
                            <div className="flex items-center space-x-3 px-2 py-2 mb-3 rounded-lg bg-secondary-50 lg:hidden">
                                <div className="h-9 w-9 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-semibold">
                                    {initials}
                                </div>
                                <div className="text-left">
                                    <div className="text-sm font-medium text-secondary-900 leading-tight">{user.name}</div>
                                    {user.username && <div className="text-xs text-secondary-600">@{user.username}</div>}
                                </div>
                            </div>
                        )}
                        <ul className="space-y-2">
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;

                                return (
                                    <li key={item.path}>
                                        <Link
                                            to={item.path}
                                            className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${isActive
                                                ? 'bg-primary-100 text-primary-700 font-medium'
                                                : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
                                                }`}
                                        >
                                            <Icon className="h-5 w-5 mr-3" />
                                            {item.label}
                                        </Link>
                                    </li>
                                );
                            })}
                            <li className="pt-2 mt-2 border-t border-secondary-200">
                                <button
                                    onClick={handleLogout}
                                    className="w-full text-left flex items-center px-4 py-2 rounded-lg transition-colors duration-200 text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                                >
                                    <LogOut className="h-5 w-5 mr-3" />
                                    Sair
                                </button>
                            </li>
                        </ul>
                    </div>
                    </div>
                </nav>

                {/* Main Content */}
                <main className="flex-1 p-4 sm:p-6 lg:p-8">
                    {children ? children : <Outlet />}
                </main>
            </div>
        </div>
    );
};

export default Layout;
